'use client';
import { addBreadcrumb, configureSignoz, reportClientError } from '@smiledragon-corp/signoz-core';
import { usePathname } from 'next/navigation';
import { type ReactNode, useEffect, useRef } from 'react';
import { describeClickTarget } from './clickTarget';

export type SignozProviderProps = {
  path: string;
  release?: string;
  enabled?: boolean;
  children?: ReactNode;
  ignoreErrors?: (string | RegExp)[];
  maxReportsPerMinute?: number;
  /**
   * 모든 리포트에 함께 전송되는 정적 태그 (예: `{ 'api.contract': '1.2.0' }`).
   * 서버에서 `frontend.tag.<key>` attribute로 승격된다.
   */
  tags?: Record<string, string>;
};

export function SignozProvider(props: SignozProviderProps): ReactNode {
  const { path, release, enabled, children, ignoreErrors, maxReportsPerMinute, tags } = props;

  // tags는 계약상 정적이다. deps에 넣으면 인라인 객체 리터럴을 넘긴 호출부에서 매 렌더마다
  // effect가 재실행되어 window 리스너가 재등록되므로, ref로 최신값을 읽고 deps에서는 뺀다.
  const tagsRef = useRef(tags);
  tagsRef.current = tags;

  useEffect(() => {
    const resolvedRelease =
      release ?? (typeof process !== 'undefined' ? process.env.NEXT_PUBLIC_BUILD_ID : undefined);
    configureSignoz({
      path,
      release: resolvedRelease,
      enabled: enabled ?? true,
      ignoreErrors,
      maxReportsPerMinute,
      tags: tagsRef.current,
      getContext: () => ({ url: location.href, userAgent: navigator.userAgent }),
    });

    const handleError = (event: ErrorEvent) => {
      reportClientError({ error: event.error ?? event.message, source: 'window.onerror' });
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      reportClientError({ error: event.reason, source: 'unhandledrejection' });
    };

    const handleClick = (event: MouseEvent) => {
      const info = describeClickTarget(event.target);
      if (info === null) return;
      addBreadcrumb({ ts: Date.now(), type: 'click', ...info });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    document.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
      document.removeEventListener('click', handleClick);
    };
  }, [path, release, enabled, ignoreErrors, maxReportsPerMinute]);

  const pathname = usePathname();
  const prevPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    const prev = prevPathnameRef.current;
    if (prev !== null && prev !== pathname) {
      addBreadcrumb({ ts: Date.now(), type: 'navigation', from: prev, to: pathname });
    }
    prevPathnameRef.current = pathname;
  }, [pathname]);

  return children;
}
